import React from 'react';
import { getTravelHero } from '../lib/eventHero';

function fmt12(t) {
  if (!t) return '';
  // Already formatted (e.g. "10:00 PM") — return as-is
  if (/[APap][Mm]/.test(t)) return t;
  const [h, m] = t.split(':').map(Number);
  if (isNaN(h) || isNaN(m)) return t;
  return `${h % 12 || 12}:${String(m).padStart(2,'0')} ${h >= 12 ? 'PM' : 'AM'}`;
}

function MapEmbed({ query, height = 200 }) {
  if (!query) return null;
  const src = `https://www.openstreetmap.org/export/embed.html?bbox=${encodeURIComponent(query)}&layer=mapnik&marker=true`;
  // Use a simpler approach — link to map with a styled placeholder
  return (
    <a href={`https://maps.apple.com/?q=${encodeURIComponent(query)}`} target="_blank" rel="noreferrer"
      style={{ display:'block', height, background:'var(--bg2)', borderRadius:'20px 20px 0 0', overflow:'hidden', position:'relative', textDecoration:'none' }}>
      <iframe
        title="Map"
        src={`https://www.google.com/maps?q=${encodeURIComponent(query)}&output=embed&z=14`}
        style={{ width:'100%', height:'100%', border:'none', filter:'brightness(0.85) contrast(1.1) saturate(0.8)', pointerEvents:'none' }}
        loading="lazy"
      />
      <div style={{ position:'absolute', bottom:0, left:0, right:0, height:60, background:'linear-gradient(transparent, var(--card))' }} />
      <div style={{ position:'absolute', top:12, right:12, background:'rgba(0,0,0,0.5)', backdropFilter:'blur(10px)', borderRadius:8, padding:'6px 10px', display:'flex', alignItems:'center', gap:4 }}>
        <i className="fa-solid fa-diamond-turn-right" style={{ fontSize:10, color:'white' }}></i>
        <span style={{ fontSize:11, fontWeight:700, color:'white' }}>Directions</span>
      </div>
    </a>
  );
}

function InfoRow({ icon, iconColor, label, value, href, green, multiline }) {
  if (!value) return null;
  const Tag = href ? 'a' : 'div';
  return (
    <Tag href={href} target={href?.startsWith('http') ? '_blank' : undefined} rel="noreferrer"
      style={{ display:'flex', alignItems: multiline ? 'flex-start' : 'center', gap:14, padding:'14px 20px', borderBottom:'1px solid var(--border)', textDecoration:'none', cursor: href ? 'pointer' : 'default' }}>
      <div style={{ width:36, height:36, borderRadius:10, background:`${iconColor||'var(--blue)'}14`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, marginTop: multiline ? 2 : 0 }}>
        <i className={`fa-solid ${icon}`} style={{ fontSize:14, color: iconColor||'var(--blue)' }}></i>
      </div>
      <div style={{ flex:1, minWidth:0 }}>
        <p style={{ fontSize:11, fontWeight:600, color:'var(--text3)', letterSpacing:1, margin:0 }}>{label}</p>
        <p style={{ fontSize: multiline ? 14 : 15, fontWeight:600, color: green ? '#22c55e' : href ? 'var(--blue)' : 'var(--text)', margin:'2px 0 0', overflow: multiline ? 'visible' : 'hidden', textOverflow: multiline ? 'unset' : 'ellipsis', whiteSpace: multiline ? 'pre-wrap' : 'nowrap', lineHeight: multiline ? 1.5 : undefined }}>{value}</p>
      </div>
      {href && <i className="fa-solid fa-chevron-right" style={{ fontSize:11, color:'var(--text3)' }}></i>}
    </Tag>
  );
}

function TravelCardContent({ r, focusLeg }) {
  if (!r) return null;

  const icons = { flight:'fa-plane', hotel:'fa-hotel', carservice:'fa-car-side', car:'fa-car', ground:'fa-van-shuttle', train:'fa-train' };
  const colors = { flight:'#1E40AF', hotel:'#06B6D4', carservice:'#06B6D4', car:'#fbbf24', ground:'#f472b6', train:'#a78bfa' };
  const typeLabel = { flight:'Flight', hotel:'Hotel', carservice:'Car Service', car:'Car Rental', ground:'Ground Transport', train:'Train' }[r.type] || r.type;
  const color = colors[r.type] || '#1E40AF';

  // Get primary location for map
  const mapQuery = r.hotelAddress || r.carServicePickup || r.pickupLocation || r.carPickupLocation || r.trainFrom ||
    (r.departureAirport ? r.departureAirport + ' Airport' : null);

  // Get phone number if any
  const phone = r.greeterPhone || r.departureGreeterPhone || r.arrivalDrivers?.[0]?.phone || r.departureDrivers?.[0]?.phone || r.carServicePhone || r.driverPhone || r.hotelPhone;
  const phoneName = r.greeterName || r.departureGreeterName || r.arrivalDrivers?.[0]?.name || r.departureDrivers?.[0]?.name || r.carServiceDriver || r.driverName || 'Call';

  const conf = r.hotelConfirmation || r.carConfirmation || r.trainConfirmation || r.flightConfirmation || r.carServiceConfirmation;

  return (
    <>
        {/* Hero — contextual image first, map fallback */}
        {(() => {
          const hero = getTravelHero(r);
          if (hero.imageUrl) return (
            <div style={{ height:180, position:'relative', borderRadius:'20px 20px 0 0', overflow:'hidden' }}>
              <div style={{ width:'100%', height:'100%', backgroundImage:`url(${hero.imageUrl})`, backgroundSize:'cover', backgroundPosition:'center', filter:'brightness(0.5) saturate(0.8)' }} />
              <div style={{ position:'absolute', bottom:0, left:0, right:0, height:60, background:'linear-gradient(transparent, var(--card))' }} />
            </div>
          );
          if (mapQuery) return <MapEmbed query={mapQuery} height={180} />;
          if (hero.gradient) return (
            <div style={{ height:140, borderRadius:'20px 20px 0 0', overflow:'hidden', background:hero.gradient, display:'flex', alignItems:'center', justifyContent:'center', position:'relative' }}>
              <i className={`fa-solid ${hero.icon}`} style={{ fontSize:48, color:'rgba(255,255,255,0.1)' }}></i>
              <div style={{ position:'absolute', bottom:0, left:0, right:0, height:40, background:'linear-gradient(transparent, var(--card))' }} />
            </div>
          );
          return null;
        })()}

        {/* Header */}
        <div style={{ padding:'20px 20px 16px', borderBottom:'1px solid var(--border)' }}>
          <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:8 }}>
            <span style={{ fontSize:10, fontWeight:700, letterSpacing:2, color, textTransform:'uppercase' }}>{typeLabel}</span>
          </div>

          {r.type === 'flight' ? (
            <>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:16, margin:'8px 0 12px' }}>
                <div style={{ textAlign:'center', maxWidth:120 }}>
                  <p style={{ fontSize:32, fontWeight:900, color:'var(--text)', margin:0, letterSpacing:-1 }}>{r.departureAirport || '—'}</p>
                  {r.departureCity && <p style={{ fontSize:11, color:'var(--text3)', margin:'2px 0 0', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.departureCity}</p>}
                  {r.flightDepartureTime && <p style={{ fontSize:13, color:'var(--text2)', margin:'4px 0 0' }}>{fmt12(r.flightDepartureTime)}</p>}
                </div>
                <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:4 }}>
                  <div style={{ width:80, height:1, background:'var(--border)', position:'relative' }}>
                    <i className="fa-solid fa-plane" style={{ position:'absolute', top:'50%', left:'50%', transform:'translate(-50%,-50%)', fontSize:14, color, background:'var(--card)', padding:'0 6px' }}></i>
                  </div>
                  <p style={{ fontSize:11, color:'var(--text3)', margin:0 }}>{r.airline} {r.flightNumber}</p>
                  {r.flightDuration && <p style={{ fontSize:10, color:'var(--text3)', margin:'2px 0 0' }}>{r.flightDuration}</p>}
                </div>
                <div style={{ textAlign:'center', maxWidth:120 }}>
                  <p style={{ fontSize:32, fontWeight:900, color:'var(--text)', margin:0, letterSpacing:-1 }}>{r.arrivalAirport || '—'}</p>
                  {r.arrivalCity && <p style={{ fontSize:11, color:'var(--text3)', margin:'2px 0 0', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.arrivalCity}</p>}
                  {r.flightArrivalTime && <p style={{ fontSize:13, color:'var(--text2)', margin:'4px 0 0' }}>{fmt12(r.flightArrivalTime)}</p>}
                </div>
              </div>
              {(r.cabinClass || r.startDate) && (
                <div style={{ textAlign:'center' }}>
                  {r.cabinClass && <p style={{ fontSize:12, fontWeight:700, color, margin:'0 0 4px' }}>{r.cabinClass} Class</p>}
                  {r.startDate && <p style={{ fontSize:13, color:'var(--text3)', margin:0 }}>
                    {new Date(r.startDate+'T12:00:00').toLocaleDateString('en-US',{weekday:'long',month:'long',day:'numeric',year:'numeric'})}
                  </p>}
                </div>
              )}
            </>
          ) : (
            <>
              <h2 style={{ fontSize:22, fontWeight:900, color:'var(--text)', margin:0, letterSpacing:-0.5 }}>
                {r.type==='carservice' ? r.carServiceCompany||'Car Service' : r.hotelName||r.carCompany||r.driverName||r.trainCompany||typeLabel}
              </h2>
              {r.hotelAddress && <p style={{ fontSize:14, color:'var(--text2)', margin:'4px 0 0' }}>{r.hotelAddress}</p>}
              {r.type==='train' && r.trainFrom && (
                <p style={{ fontSize:14, color:'var(--text2)', margin:'4px 0 0' }}>{r.trainFrom} → {r.trainTo}</p>
              )}
            </>
          )}
        </div>

        {/* Action buttons */}
        <div style={{ display:'flex', gap:8, padding:'16px 20px', borderBottom:'1px solid var(--border)' }}>
          {mapQuery && (
            <a href={`https://maps.apple.com/?q=${encodeURIComponent(mapQuery)}`} target="_blank" rel="noreferrer"
              style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:8, padding:'12px', borderRadius:12, background:'var(--blue)', color:'white', fontWeight:700, fontSize:14, textDecoration:'none', fontFamily:'inherit' }}>
              <i className="fa-solid fa-diamond-turn-right" style={{ fontSize:12 }}></i> Directions
            </a>
          )}
          {phone && (
            <a href={`tel:${phone.replace(/\D/g,'')}`}
              style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:8, padding:'12px', borderRadius:12, background:'rgba(34,197,94,0.12)', border:'1px solid rgba(34,197,94,0.25)', color:'#22c55e', fontWeight:700, fontSize:14, textDecoration:'none', fontFamily:'inherit' }}>
              <i className="fa-solid fa-phone" style={{ fontSize:12 }}></i> Call {phoneName.split(' ')[0]}
            </a>
          )}
        </div>

        {/* Details */}
        <div>
          {/* Flight-specific */}
          {r.type === 'flight' && <>
            {(!focusLeg || focusLeg === 'departure') && (
              <InfoRow icon="fa-plane-departure" iconColor="#1E40AF" label="DEPARTURE" value={r.departureAirport ? `${r.departureAirport} Airport${r.flightDepartureTime ? ' · '+fmt12(r.flightDepartureTime) : ''}` : null}
                href={r.departureAirport ? `https://maps.apple.com/?q=${encodeURIComponent(r.departureAirport+' Airport')}` : null} />
            )}
            {(!focusLeg || focusLeg === 'arrival') && (
              <InfoRow icon="fa-plane-arrival" iconColor="#22c55e" label="ARRIVAL" value={r.arrivalAirport ? `${r.arrivalAirport} Airport${r.flightArrivalTime ? ' · '+fmt12(r.flightArrivalTime) : ''}` : null}
                href={r.arrivalAirport ? `https://maps.apple.com/?q=${encodeURIComponent(r.arrivalAirport+' Airport')}` : null} />
            )}
            <InfoRow icon="fa-users" label={r.passenger?.includes(',') ? 'PASSENGERS' : 'PASSENGER'} value={r.passenger} />
            {/* Private aviation details */}
            {r.flightType === 'private' && <>
              <InfoRow icon="fa-jet-fighter" iconColor="#a78bfa" label="TAIL NUMBER" value={r.tailNumber} />
              <InfoRow icon="fa-plane" iconColor="#a78bfa" label="AIRCRAFT" value={r.aircraftType} />
              {r.operator && <InfoRow icon="fa-building" label="OPERATOR" value={r.operator} />}
              {r.departureFBO && <InfoRow icon="fa-warehouse" label="DEPARTURE FBO" value={`${r.departureFBO}${r.departureFBOPhone ? ' · ' + r.departureFBOPhone : ''}`}
                href={r.departureFBOAddress ? `https://maps.apple.com/?q=${encodeURIComponent(r.departureFBOAddress)}` : null} />}
              {r.arrivalFBO && <InfoRow icon="fa-warehouse" iconColor="#22c55e" label="ARRIVAL FBO" value={`${r.arrivalFBO}${r.arrivalFBOPhone ? ' · ' + r.arrivalFBOPhone : ''}`}
                href={r.arrivalFBOAddress ? `https://maps.apple.com/?q=${encodeURIComponent(r.arrivalFBOAddress)}` : null} />}
              {r.requestedBy && <InfoRow icon="fa-address-book" label="REQUESTED BY" value={`${r.requestedBy}${r.requestedByPhone ? ' · ' + r.requestedByPhone : ''}`}
                href={r.requestedByPhone ? `tel:${r.requestedByPhone.replace(/\D/g,'')}` : null} />}
            </>}
            {/* Commercial details */}
            {r.flightType !== 'private' && <InfoRow icon="fa-chair" label="SEAT" value={r.seat} />}
            <InfoRow icon="fa-hashtag" label={r.flightType === 'private' ? 'RESERVATION' : 'CONFIRMATION'} value={r.flightConfirmation} />
            {/* Departure greeter & drivers */}
            {(r.departureGreeterName || r.departureGreeterPhone) && (
              <InfoRow icon="fa-hand-wave" iconColor="#1E40AF" label="DEPARTURE GREETER" value={`${r.departureGreeterName||''}${r.departureGreeterPhone?' · '+r.departureGreeterPhone:''}`} href={r.departureGreeterPhone?`tel:${r.departureGreeterPhone.replace(/\D/g,'')}`:'#'} />
            )}
            {r.departureDrivers?.map((d,i) => (
              <InfoRow key={`dd-${i}`} icon="fa-car" iconColor="#1E40AF" label={`DEPARTURE DRIVER${r.departureDrivers.length>1?' '+(i+1):''}`} value={`${d.name}${d.phone?' · '+d.phone:''}`} href={d.phone?`tel:${d.phone.replace(/\D/g,'')}`:'#'} />
            ))}
            {/* Arrival greeter & drivers */}
            {(r.greeterName || r.greeterPhone) && (
              <InfoRow icon="fa-hand-wave" iconColor="#22c55e" label="ARRIVAL GREETER" value={`${r.greeterName||''}${r.greeterPhone?' · '+r.greeterPhone:''}`} href={r.greeterPhone?`tel:${r.greeterPhone.replace(/\D/g,'')}`:'#'} green />
            )}
            {r.arrivalDrivers?.map((d,i) => (
              <InfoRow key={`ad-${i}`} icon="fa-car" iconColor="#22c55e" label={`ARRIVAL DRIVER${r.arrivalDrivers.length>1?' '+(i+1):''}`} value={`${d.name}${d.phone?' · '+d.phone:''}`} href={d.phone?`tel:${d.phone.replace(/\D/g,'')}`:'#'} green />
            ))}
          </>}

          {/* Hotel-specific */}
          {r.type === 'hotel' && <>
            <InfoRow icon="fa-right-to-bracket" label="CHECK-IN" value={r.startDate ? new Date(r.startDate+'T12:00:00').toLocaleDateString('en-US',{weekday:'long',month:'short',day:'numeric'}) : null} />
            <InfoRow icon="fa-right-from-bracket" label="CHECK-OUT" value={r.endDate ? new Date(r.endDate+'T12:00:00').toLocaleDateString('en-US',{weekday:'long',month:'short',day:'numeric'}) : null} />
            <InfoRow icon="fa-bed" label="ROOM" value={r.roomType} />
            <InfoRow icon="fa-hashtag" label="CONFIRMATION" value={r.hotelConfirmation} />
          </>}

          {/* Car service */}
          {r.type === 'carservice' && <>
            <InfoRow icon="fa-user" label="DRIVER" value={r.carServiceDriver} />
            <InfoRow icon="fa-location-dot" label="PICKUP" value={r.carServicePickup} href={r.carServicePickup?`https://maps.apple.com/?q=${encodeURIComponent(r.carServicePickup)}`:null} />
            <InfoRow icon="fa-flag-checkered" label="DROPOFF" value={r.carServiceDropoff} href={r.carServiceDropoff?`https://maps.apple.com/?q=${encodeURIComponent(r.carServiceDropoff)}`:null} />
            <InfoRow icon="fa-clock" label="PICKUP TIME" value={r.carServiceTime ? fmt12(r.carServiceTime) : null} />
            <InfoRow icon="fa-hashtag" label="CONFIRMATION" value={r.carServiceConfirmation} />
          </>}

          {/* Ground */}
          {r.type === 'ground' && <>
            <InfoRow icon="fa-location-dot" label="PICKUP" value={r.pickupLocation} href={r.pickupLocation?`https://maps.apple.com/?q=${encodeURIComponent(r.pickupLocation)}`:null} />
            <InfoRow icon="fa-flag-checkered" label="DROPOFF" value={r.dropoffLocation} href={r.dropoffLocation?`https://maps.apple.com/?q=${encodeURIComponent(r.dropoffLocation)}`:null} />
          </>}

          {/* Car rental */}
          {r.type === 'car' && <>
            <InfoRow icon="fa-location-dot" label="PICKUP" value={r.carPickupLocation} href={r.carPickupLocation?`https://maps.apple.com/?q=${encodeURIComponent(r.carPickupLocation)}`:null} />
            <InfoRow icon="fa-flag-checkered" label="DROPOFF" value={r.carDropoffLocation} href={r.carDropoffLocation?`https://maps.apple.com/?q=${encodeURIComponent(r.carDropoffLocation)}`:null} />
            <InfoRow icon="fa-hashtag" label="CONFIRMATION" value={r.carConfirmation} />
          </>}

          {/* Train */}
          {r.type === 'train' && <>
            <InfoRow icon="fa-clock" label="DEPARTURE" value={r.departureTime ? fmt12(r.departureTime) : null} />
            <InfoRow icon="fa-clock" label="ARRIVAL" value={r.arrivalTime ? fmt12(r.arrivalTime) : null} />
            <InfoRow icon="fa-hashtag" label="CONFIRMATION" value={r.trainConfirmation} />
          </>}

          {/* Shared */}
          {r.type !== 'flight' && r.passenger && (
            <InfoRow icon="fa-users" label={r.passenger.includes(',') ? 'PASSENGERS' : 'FOR'} value={r.passenger} />
          )}
          {r.notes && <InfoRow icon="fa-note-sticky" label="NOTES" value={r.notes} multiline />}

          {/* Files */}
          {r.files?.length > 0 && (
            <div style={{ padding:'14px 20px', borderTop:'1px solid var(--border)' }}>
              <p style={{ fontSize:11, fontWeight:600, color:'var(--text3)', letterSpacing:1, marginBottom:8 }}>DOCUMENTS</p>
              <div style={{ display:'flex', gap:8, overflowX:'auto' }}>
                {r.files.map((f, i) => (
                  <button key={i} onClick={() => {
                    if (f.url) window.open((f.url.startsWith('http')?'':process.env.REACT_APP_API_URL||'http://localhost:3001')+f.url, '_blank');
                    else if (f.data) { const a=f.data.split(','); const m=a[0].match(/:(.*?);/)[1]; const b=atob(a[1]); const u=new Uint8Array(b.length); for(let j=0;j<b.length;j++) u[j]=b.charCodeAt(j); window.open(URL.createObjectURL(new Blob([u],{type:m})),'_blank'); }
                  }}
                    style={{ flexShrink:0, display:'flex', alignItems:'center', gap:8, padding:'10px 14px', background:'var(--bg)', borderRadius:10, border:'1px solid var(--border)', cursor:'pointer', fontFamily:'inherit' }}>
                    <i className={`fa-solid ${f.type?.includes('pdf')?'fa-file-pdf':f.type?.startsWith('image/')?'fa-image':'fa-file'}`} style={{ fontSize:14, color }}></i>
                    <span style={{ fontSize:12, fontWeight:600, color:'var(--text)' }}>{f.name?.replace(/\.[^.]+$/,'')||'Document'}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

    </>
  );
}

function CardPeekHeader({ r, onClick, style }) {
  const icons = { flight:'fa-plane', hotel:'fa-hotel', carservice:'fa-car-side', car:'fa-car', ground:'fa-van-shuttle', train:'fa-train' };
  const colors = { flight:'#1E40AF', hotel:'#06B6D4', carservice:'#06B6D4', car:'#fbbf24', ground:'#f472b6', train:'#a78bfa' };
  const c = colors[r.type] || '#1E40AF';
  const summary = r.type === 'flight' ? `${r.airline||''} ${r.flightNumber||''} · ${r.departureAirport||'?'} → ${r.arrivalAirport||'?'}`.trim()
    : r.type === 'hotel' ? r.hotelName || 'Hotel'
    : r.type === 'carservice' ? r.carServiceCompany || 'Car Service'
    : r.type === 'car' ? r.carCompany || 'Car Rental'
    : r.type === 'ground' ? r.driverName || 'Ground'
    : r.trainCompany || 'Train';

  return (
    <div onClick={onClick} style={{ display:'flex', alignItems:'center', gap:10, padding:'14px 20px', cursor:'pointer', ...style }}>
      <div style={{ width:32, height:32, borderRadius:10, background:`${c}18`, border:`1.5px solid ${c}44`,
        display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
        <i className={`fa-solid ${icons[r.type]||'fa-circle'}`} style={{ fontSize:14, color:c }}></i>
      </div>
      <div style={{ flex:1, minWidth:0 }}>
        <p style={{ fontSize:14, fontWeight:800, color:'var(--text)', margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{summary}</p>
        {r.flightDepartureTime && (
          <p style={{ fontSize:11, color:'var(--text3)', margin:'2px 0 0' }}>{fmt12(r.flightDepartureTime)}{r.flightArrivalTime ? ' → '+fmt12(r.flightArrivalTime) : ''}</p>
        )}
      </div>
      <i className="fa-solid fa-chevron-down" style={{ fontSize:10, color:'var(--text3)' }}></i>
    </div>
  );
}

export default function TravelModal({ record, focusLeg, onClose }) {
  if (!record) return null;

  const records = Array.isArray(record) ? record : [record];
  const [activeIdx, setActiveIdx] = React.useState(0);
  const isMulti = records.length > 1;

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.8)', zIndex:2000, display:'flex', alignItems:'flex-end', justifyContent:'center' }}
      onMouseDown={e => e.target === e.currentTarget && onClose()}>
      {/* Floating X button */}
      <button onClick={onClose}
        style={{ position:'absolute', top:16, left:16, width:36, height:36, borderRadius:'50%',
          background:'rgba(255,255,255,0.12)', backdropFilter:'blur(10px)', WebkitBackdropFilter:'blur(10px)',
          border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center',
          zIndex:2010, transition:'background 0.15s' }}
        onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,0.2)'}
        onMouseLeave={e => e.currentTarget.style.background='rgba(255,255,255,0.12)'}>
        <i className="fa-solid fa-xmark" style={{ fontSize:16, color:'#fff' }}></i>
      </button>
      <div style={{ width:'100%', maxWidth:480, maxHeight:'92vh', display:'flex', flexDirection:'column', paddingBottom:'calc(88px + env(safe-area-inset-bottom, 0px))' }}>

        {isMulti ? (
          <>
            {/* Wallet stack — cards behind peek out at top */}
            {/* Stacked peek cards (inactive) */}
            {records.map((r, i) => {
              if (i === activeIdx) return null;
              const colors = { flight:'#1E40AF', hotel:'#06B6D4', carservice:'#06B6D4', car:'#fbbf24', ground:'#f472b6', train:'#a78bfa' };
              const c = colors[r.type] || '#1E40AF';
              return (
                <div key={r.id || i}
                  style={{ background:'var(--card)', borderRadius:'16px 16px 0 0', border:'1px solid var(--border)', borderBottom:'none',
                    marginBottom:-4, position:'relative', zIndex: i,
                    boxShadow:'0 -2px 10px rgba(0,0,0,0.15)',
                    transition:'all 0.3s ease' }}>
                  <CardPeekHeader r={r} onClick={() => setActiveIdx(i)}
                    style={{ borderLeft:`3px solid ${c}`, borderRadius:'16px 16px 0 0' }} />
                </div>
              );
            })}

            {/* Active expanded card */}
            {(() => {
              const r = records[activeIdx];
              return (
                <div key={r.id || activeIdx}
                  style={{ background:'var(--card)', borderRadius:'20px 20px 0 0', border:'1px solid var(--border)', borderBottom:'none',
                    flex:1, overflowY:'auto', position:'relative', zIndex: records.length + 1,
                    boxShadow:'0 -4px 20px rgba(0,0,0,0.3)',
                    transition:'all 0.3s ease' }}>
                  {/* Handle */}
                  <div style={{ position:'sticky', top:0, zIndex:2, background:'var(--card)', padding:'10px 0 0', borderRadius:'20px 20px 0 0' }}>
                    <div style={{ width:36, height:4, background:'var(--border)', borderRadius:2, margin:'0 auto' }} />
                  </div>
                  <TravelCardContent r={r} focusLeg={focusLeg} />
                  {/* Close */}
                  <div style={{ padding:'16px 20px 32px' }}>
                    <button onClick={onClose}
                      style={{ width:'100%', padding:'14px', borderRadius:14, background:'var(--bg)', border:'1px solid var(--border)', color:'var(--text2)', fontFamily:'inherit', fontSize:15, fontWeight:700, cursor:'pointer', transition:'background 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,0.04)'}
                      onMouseLeave={e => e.currentTarget.style.background='var(--bg)'}>
                      Close
                    </button>
                  </div>
                </div>
              );
            })()}
          </>
        ) : (
          // Single record — normal modal
          <div style={{ background:'var(--card)', borderRadius:'20px 20px 0 0', border:'1px solid var(--border)', borderBottom:'none',
            flex:1, overflowY:'auto' }}>
            <div style={{ position:'sticky', top:0, zIndex:2, background:'var(--card)', padding:'10px 0 0', borderRadius:'20px 20px 0 0' }}>
              <div style={{ width:36, height:4, background:'var(--border)', borderRadius:2, margin:'0 auto' }} />
            </div>
            <TravelCardContent r={records[0]} focusLeg={focusLeg} />
            <div style={{ padding:'16px 20px 32px' }}>
              <button onClick={onClose}
                style={{ width:'100%', padding:'14px', borderRadius:14, background:'var(--bg)', border:'1px solid var(--border)', color:'var(--text2)', fontFamily:'inherit', fontSize:15, fontWeight:700, cursor:'pointer', transition:'background 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,0.04)'}
                onMouseLeave={e => e.currentTarget.style.background='var(--bg)'}>
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
